import React, { useId, useMemo } from "react";
import { Box } from "@mui/material";
import { alpha } from "@mui/material/styles";

interface SparklineProps {
  data: number[];
  color: string;
  height?: number;
  /** Fixed width in px; omit to stretch to the container. */
  width?: number;
  /** Soft gradient area under the line. */
  fill?: boolean;
  /** Luminous line (drop-shadow in the line color). */
  glow?: boolean;
  strokeWidth?: number;
  /** Marker on the last point. */
  dot?: boolean;
}

const VIEW_W = 300;

/**
 * Lightweight trend line. Stretches to its container with a constant stroke
 * width; the end dot is HTML so it never distorts.
 */
export const Sparkline: React.FC<SparklineProps> = ({
  data,
  color,
  height = 40,
  width,
  fill = false,
  glow = false,
  strokeWidth = 1.75,
  dot = false,
}) => {
  const id = useId().replace(/:/g, "");
  const geometry = useMemo(() => {
    const clean = data.filter((v) => Number.isFinite(v));
    if (clean.length < 2) return null;
    const min = Math.min(...clean);
    const max = Math.max(...clean);
    const span = max - min || 1;
    const pad = 3;
    const pts = clean.map((v, i) => ({
      x: (VIEW_W * i) / (clean.length - 1),
      y: pad + (height - pad * 2) * (1 - (v - min) / span),
    }));
    let d = `M${pts[0].x.toFixed(1)},${pts[0].y.toFixed(1)}`;
    for (let i = 1; i < pts.length; i++) {
      const p0 = pts[i - 1];
      const p1 = pts[i];
      const cx = (p0.x + p1.x) / 2;
      d += ` C${cx.toFixed(1)},${p0.y.toFixed(1)} ${cx.toFixed(1)},${p1.y.toFixed(1)} ${p1.x.toFixed(1)},${p1.y.toFixed(1)}`;
    }
    return { d, last: pts[pts.length - 1] };
  }, [data, height]);

  if (!geometry) return <Box sx={{ height }} />;

  return (
    <Box sx={{ position: "relative", width: width ?? "100%", height, flexShrink: 0, pr: dot ? "10px" : 0, boxSizing: "border-box" }}>
      <svg
        width="100%"
        height={height}
        viewBox={`0 0 ${VIEW_W} ${height}`}
        preserveAspectRatio="none"
        style={{ display: "block", overflow: "visible" }}
        aria-hidden
      >
        {fill && (
          <>
            <defs>
              <linearGradient id={`spark-${id}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={color} stopOpacity={0.28} />
                <stop offset="100%" stopColor={color} stopOpacity={0} />
              </linearGradient>
            </defs>
            <path d={`${geometry.d} L${VIEW_W},${height} L0,${height} Z`} fill={`url(#spark-${id})`} />
          </>
        )}
        <path
          d={geometry.d}
          fill="none"
          stroke={color}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeLinejoin="round"
          vectorEffect="non-scaling-stroke"
          style={glow ? { filter: `drop-shadow(0 0 5px ${alpha(color, 0.75)})` } : undefined}
        />
      </svg>
      {dot && (
        <Box
          sx={{
            position: "absolute",
            right: 10,
            top: geometry.last.y,
            width: 7,
            height: 7,
            borderRadius: "50%",
            bgcolor: color,
            transform: "translate(50%, -50%)",
            boxShadow: `0 0 0 3px ${alpha(color, 0.22)}`,
          }}
        />
      )}
    </Box>
  );
};

export default Sparkline;
